import React, { useCallback } from "react";
import { Typography, Chip, Stack } from "@mui/material";
import { Warning as ExpiredIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SchoolStatusTable from "../components/schools/SchoolStatusTable";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
};

const ExpiredSchools = () => {
  const navigate = useNavigate();

  const fetchRows = useCallback(
    () => apiService.getExpiredSchools({ limit: 500, offset: 0 }),
    []
  );

  return (
    <SchoolStatusTable
      title="Expired Schools"
      subtitle="Schools past grace — Service Expired (renewal needed)"
      breadcrumbLabel="Expired Schools"
      breadcrumbIcon={<ExpiredIcon sx={{ color: "#ffffff", fontSize: 18 }} />}
      fetchRows={fetchRows}
      onRowClick={(row) => {
        if (row?.school_code) {
          navigate(`/schools/detail/${encodeURIComponent(row.school_code)}`);
        }
      }}
      searchFields={(row) => [
        row.school_name,
        row.school_code,
        row.admin_name,
        row.admin_phone,
        row.state,
        row.country,
        row.product_code,
        row.product_name,
        row.order_id,
      ]}
      columns={[
        {
          id: "name",
          label: "School",
          render: (row) => (
            <Stack spacing={0.15}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {row.school_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.school_code || "—"}
              </Typography>
            </Stack>
          ),
        },
        {
          id: "contact",
          label: "Contact",
          render: (row) => (
            <Stack spacing={0.15}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.admin_name || "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.admin_phone || "—"}
              </Typography>
            </Stack>
          ),
        },
        {
          id: "product",
          label: "Product",
          render: (row) => (
            <Stack spacing={0.15}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.product_name || row.product_code || "—"}
              </Typography>
              {row.product_name && row.product_code ? (
                <Typography variant="caption" color="text.secondary">
                  {row.product_code}
                </Typography>
              ) : null}
            </Stack>
          ),
        },
        {
          id: "status",
          label: "Status",
          render: () => (
            <Chip
              size="small"
              label="Service Expired"
              sx={{
                fontWeight: 800,
                height: 24,
                borderRadius: "8px",
                color: "#fff",
                bgcolor: crmColors.error.main,
              }}
            />
          ),
        },
        {
          id: "location",
          label: "Location",
          render: (row) => [row.state, row.country].filter(Boolean).join(", ") || "—",
        },
        {
          id: "expired",
          label: "Service Ended",
          render: (row) => (
            <Stack spacing={0.15}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDate(row.service_expiry || row.service_end_date)}
              </Typography>
              {row.grace_end_date ? (
                <Typography variant="caption" color="text.secondary">
                  Grace ended {formatDate(row.grace_end_date)}
                </Typography>
              ) : null}
            </Stack>
          ),
        },
        {
          id: "overdue",
          label: "Overdue",
          render: (row) =>
            row.days_overdue != null ? (
              <Chip
                size="small"
                label={`${row.days_overdue}d`}
                sx={{
                  fontWeight: 800,
                  height: 22,
                  color: "#fff",
                  bgcolor: crmColors.error.dark,
                  borderRadius: "7px",
                }}
              />
            ) : (
              "—"
            ),
        },
        {
          id: "order",
          label: "Last Order",
          render: (row) => row.order_id || "—",
        },
      ]}
    />
  );
};

export default ExpiredSchools;
